import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { first, share } from 'rxjs/operators'
import { io, Socket } from 'socket.io-client'

@Injectable({ providedIn: 'root' })
export class SocketService {
  /** Socket reference */
  public ioSocket: Socket

  /** Record that counts subscribers per event */
  private subscribersCounter: Record<string, number> = {}

  /** Record of subscribed events */
  private eventObservables$: Record<string, Observable<any>> = {}

  /** Removes the specified listener from the listener array for the event named eventName
   * @param { string } namespace
   * @param { (...args: any[]) => void) } callback
   */
  public off(namespace: string, callback: (...args: any[]) => void): void {
    this.ioSocket.off(namespace, callback)
  }

  /** Adds the listener function to the end of the listeners array for the event named eventName
   * @param { string }
   * @param { (...args: any[]) => void) }
   */
  public on(eventName: string, callback: (...args: any[]) => void): void {
    this.ioSocket.on(eventName, callback)
  }

  /** Adds a one-time listener function for the event named eventName
   * @param { string } eventName
   * @param { (...args: any[]) => void) } callback
   */
  public once(eventName: string, callback: (...args: any[]) => void): void {
    this.ioSocket.once(eventName, callback)
  }

  /** Instantiate and connect to io socket
   * @param { string } url
   * @param { string } token
   */
  public connect(url: string, token: string): void {
    this.ioSocket = io(url, {
      autoConnect: false,
      path: '/iot/socket.io',
      transports: ['polling'],
      transportOptions: {
        polling: {
          extraHeaders: {
            Authorization: `Bearer ${token}`,
          },
        },
      },
    })

    this.ioSocket.connect()
  }

  /** Disconnect io socket connection */
  public disconnect(): Socket<any> {
    return this.ioSocket.disconnect()
  }

  /** Sends a message to the server
   * @param { string } eventName
   * @param { any[] } ...args
   */
  public emit(eventName: string, ...args: any[]): void {
    this.ioSocket.emit(eventName, ...args)
  }

  /** Remove listener by event name */
  public removeListener(eventName: string): void {
    ;(this.ioSocket as any).removeListener(eventName)
  }

  /** Removes all listeners, or those of the specified eventName
   * @param { string } eventNames
   */
  public removeAllListeners(eventNames?: string[]): void {
    ;(this.ioSocket as any).removeAllListeners(eventNames)
  }

  /** Takes an event name and returns an Observable that you can subscribe to
   * @param { string } eventName
   * @returns { Observable<T> }
   */
  public fromEvent<T>(eventName: string): Observable<T> {
    if (!this.subscribersCounter[eventName]) {
      this.subscribersCounter[eventName] = 0
    }
    this.subscribersCounter[eventName]++

    if (!this.eventObservables$[eventName]) {
      this.eventObservables$[eventName] = new Observable((observer: any) => {
        const listener = (data: T): void => {
          observer.next(data)
        }
        this.ioSocket.on(eventName, listener)
        return () => {
          this.subscribersCounter[eventName]--
          if (this.subscribersCounter[eventName] === 0) {
            ;(this.ioSocket as any).removeListener(eventName, listener)
            delete this.eventObservables$[eventName]
          }
        }
      }).pipe(share())
    }
    return this.eventObservables$[eventName]
  }

  /** Creates a Observanle for a one-time event
   * @param { string } eventName
   * @returns { Observable<T> }
   */
  public fromOneTimeEvent<T>(eventName: string): Observable<T> {
    const observable = new Observable<T>((observer) =>
      this.once(eventName, (result: T) => {
        observer.next(result)
      })
    )
    return observable.pipe(first())
  }
}
